import { useState, useEffect } from "react";
import {
  Clock,
  Award,
  AlertTriangle,
  PlayCircle,
  Flag,
  BookOpen,
  Users,
  Target,
} from "lucide-react";
import {
  createQuizAttempt,
  fetchQuizDetails,
} from "../../Services/quizeServices";
import { useLocation, useNavigate } from "react-router-dom";

export default function QuizDetail() {
  const [quizData, setQuizData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const quizId = queryParams.get("quiz_id");

  // Fetch quiz details on component mount
  useEffect(() => {
    const getQuizData = async () => {
      try {
        setLoading(true);
        const data = await fetchQuizDetails(quizId);
        setQuizData(data);
        setError(null);
      } catch (err) {
        setError("Failed to load quiz details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    getQuizData();
  }, [quizId]);

  // Handle starting a quiz attempt
  const handleStartAttempt = async (action = "start") => {
    try {
      setLoading(true);
      const attempt = await createQuizAttempt(quizId);
      navigate(
        `/quize/attempt?quize_id=${quizId}&attempt_id=${attempt.attempt_id}&action=${action}`
      );
    } catch (err) {
      setError("Failed to start quiz attempt. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle continuing an existing attempt
  const handleContinueAttempt = (attemptId) => {
    navigate(
      `/quize/attempt?quize_id=${quizId}&attempt_id=${attemptId}&action=continue`
    );
  };

  // Handle review an existing attempt
  const handleReviewAttempt = (attemptId) => {
    navigate(
      `/quize/attempt?quize_id=${quizId}&attempt_id=${attemptId}&action=review`
    );
  };

  // Format time from minutes to "X min" display
  const formatTime = (minutes) => {
    return `${minutes} min`;
  };

  // Calculate progress for past attempts
  const calculateProgress = (mark, totalMark) => {
    return ((mark / totalMark) * 100).toFixed(2);
  };

  // Find incomplete attempts
  const findIncompleteAttempt = () => {
    if (!quizData || !quizData.attempt || quizData.attempt.length === 0)
      return null;

    // Find the most recent incomplete attempt (is_completed = 0)
    return quizData.attempt.find((attempt) => attempt.is_completed === 0);
  };

  // Get highest score
  const getHighestScore = () => {
    if (!quizData || !quizData.attempt || quizData.attempt.length === 0)
      return null;
    const completedAttempts = quizData.attempt.filter(
      (a) => a.is_completed === 1
    );
    if (completedAttempts.length === 0) return null;
    return Math.max(
      ...completedAttempts.map((a) => (a.mark / a.totalMark) * 100)
    );
  };

  if (loading) {
    return (
      <div className="min-h-96 flex items-center justify-center bg-gradient-to-br rounded-xl mx-4 sm:mx-0">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium text-sm sm:text-base">
            Loading quiz details...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gradient-to-r from-red-50 to-red-100 p-4 sm:p-6 rounded-xl border border-red-200 shadow-sm mx-4 sm:mx-0">
        <div className="flex items-start sm:items-center text-red-700">
          <div className="bg-red-200 p-2 rounded-full mr-3 sm:mr-4 flex-shrink-0">
            <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6" />
          </div>
          <div>
            <h3 className="font-semibold text-base sm:text-lg">
              Unable to Load Quiz
            </h3>
            <p className="text-red-600 mt-1 text-sm sm:text-base">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!quizData) return null;

  const { quiz, attempt, attemptReminging } = quizData;
  const incompleteAttempt = findIncompleteAttempt();
  const highestScore = getHighestScore();
  // const displayScore = highestScore === "NaN" ? 0 : highestScore;
  const displayScore = Number.isNaN(highestScore) ? 0 : highestScore;


  return (
    <div className=" max-w-screen-lg w-full bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden border border-gray-200 mx-4 sm:mx-6 lg:mx-auto">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 p-4 sm:p-6 lg:p-8 text-white">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center mb-3">
              <div className="bg-blue-500 bg-opacity-30 p-1.5 sm:p-2 rounded-lg mr-2 sm:mr-3">
                <BookOpen className="w-4 h-4 sm:w-6 sm:h-6" />
              </div>
              <span className="text-blue-100 text-xs sm:text-sm font-medium tracking-wide uppercase">
                Quiz Assessment
              </span>
            </div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-4 leading-tight pr-4">
              {quiz.title}
            </h1>

            {/* Quiz Stats Grid - Mobile: 1 column, Tablet: 2 columns, Desktop: 3 columns */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-4 sm:mt-6">
              <div className="bg-blue-500 bg-opacity-20 rounded-lg p-3 sm:p-4 backdrop-blur-sm">
                <div className="flex items-center">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-200 flex-shrink-0" />
                  <div>
                    <p className="text-blue-100 text-xs sm:text-sm">
                      Time Limit
                    </p>
                    <p className="text-lg sm:text-xl font-bold">
                      {formatTime(quiz.time_limit)}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-blue-500 bg-opacity-20 rounded-lg p-3 sm:p-4 backdrop-blur-sm">
                <div className="flex items-center">
                  <Target className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-200 flex-shrink-0" />
                  <div>
                    <p className="text-blue-100 text-xs sm:text-sm">
                      Attempts Left
                    </p>
                    <p className="text-lg sm:text-xl font-bold">
                      {attemptReminging}
                    </p>
                  </div>
                </div>
              </div>
              {highestScore !== null && (
                <div className="bg-blue-500 bg-opacity-20 rounded-lg p-3 sm:p-4 backdrop-blur-sm sm:col-span-2 lg:col-span-1">
                  <div className="flex items-center">
                    <Award className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-yellow-300 flex-shrink-0" />
                    <div>
                      <p className="text-blue-100 text-xs sm:text-sm">
                        Best Score
                      </p>
                      <p className="text-lg sm:text-xl font-bold">
                        {displayScore.toFixed()}%
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 lg:p-8">
        {/* No Attempts Message */}
        {(!attempt || attempt.length === 0) && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8 border border-blue-100">
            <div className="text-center">
              <div className="bg-blue-100 p-2 sm:p-3 rounded-full w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 flex items-center justify-center">
                <PlayCircle className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
                Ready to Begin?
              </h3>
              <p className="text-gray-600 mb-4 text-sm sm:text-base">
                This quiz will test your knowledge and understanding. Make sure
                you're in a quiet environment and have enough time to complete
                it within the {formatTime(quiz.time_limit)} time limit.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center text-xs sm:text-sm text-gray-500 space-y-2 sm:space-y-0 sm:space-x-6">
                <div className="flex items-center">
                  <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  <span>{formatTime(quiz.time_limit)} to complete</span>
                </div>
                <div className="flex items-center">
                  <Target className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  <span>{attemptReminging} attempts available</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Previous Attempts */}
        {attempt && attempt.length > 0 && (
          <div className="mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2 sm:mb-0">
                Your Progress
              </h2>
              <span className="text-xs sm:text-sm text-gray-500 bg-gray-100 px-2 sm:px-3 py-1 rounded-full self-start sm:self-center">
                {attempt.length} {attempt.length === 1 ? "attempt" : "attempts"}{" "}
                made
              </span>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {attempt.map((a, index) => {
                const score = ((a.mark / a.totalMark) * 100).toFixed();
                const isIncomplete = a.is_completed === 0;
                const displayScoreForAttempt = Number.isNaN(score) ? 0 : score;


                return (
                  <div
                    key={a.attempt_id}
                    className={`relative bg-white border-2 rounded-lg sm:rounded-xl p-4 sm:p-6 transition-all hover:shadow-md ${
                      isIncomplete
                        ? "border-amber-200 bg-amber-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    {/* Attempt Header */}
                    <div className="flex items-start sm:items-center justify-between mb-3 sm:mb-4">
                      <div className="flex items-start sm:items-center">
                        <div
                          className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white font-bold mr-3 sm:mr-4 flex-shrink-0 ${
                            isIncomplete
                              ? "bg-amber-500"
                              : score >= 80
                              ? "bg-green-500"
                              : score >= 60
                              ? "bg-blue-500"
                              : "bg-red-500"
                          }`}
                        >
                          {attempt.length - index}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800 text-sm sm:text-base">
                            {isIncomplete
                              ? "In Progress"
                              : `Attempt ${attempt.length - index}`}
                          </p>
                          <p className="text-xs sm:text-sm text-gray-500">
                            {new Date(a.attempt_date).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Action Button and Score */}
                      <div className="flex flex-col sm:flex-row items-end sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                        {!isIncomplete && (
                          <div className="text-right">
                            <div className="flex items-center">
                              <Award className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-yellow-500" />
                              <span className="text-lg sm:text-2xl font-bold text-gray-800">
                                {displayScoreForAttempt}%
                              </span>
                            </div>
                            <p className="text-xs sm:text-sm text-gray-500">
                              {a.mark} / {a.totalMark} points
                            </p>
                          </div>
                        )}

                        {isIncomplete ? (
                          <button
                            onClick={() => handleContinueAttempt(a.attempt_id)}
                            className="bg-amber-500 hover:bg-amber-600 text-white font-semibold py-2 px-4 sm:px-6 rounded-lg transition flex items-center shadow-sm text-sm sm:text-base"
                          >
                            <PlayCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                            Continue
                          </button>
                        ) : (
                          <button
                            onClick={() => handleReviewAttempt(a.attempt_id)}
                            className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2 px-4 sm:px-6 rounded-lg transition flex items-center text-sm sm:text-base"
                          >
                            <Flag className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                            Review
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Progress Bar */}
                    {!isIncomplete && (
                      <div className="mb-3 sm:mb-4">
                        <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3">
                          <div
                            className={`h-2 sm:h-3 rounded-full transition-all ${
                              score >= 80
                                ? "bg-green-500"
                                : score >= 60
                                ? "bg-blue-500"
                                : "bg-red-500"
                            }`}
                            style={{
                              width: `${calculateProgress(
                                a.mark,
                                a.totalMark
                              )}%`,
                            }}
                          ></div>
                        </div>
                      </div>
                    )}

                    {/* Time Spent */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between text-xs sm:text-sm space-y-2 sm:space-y-0">
                      <div className="flex items-center text-gray-600">
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        <span>Time spent: {a.time_spent} min</span>
                      </div>

                      {isIncomplete && (
                        <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 self-start sm:self-center">
                          <div className="w-2 h-2 bg-amber-400 rounded-full mr-2 animate-pulse"></div>
                          In Progress
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Action Section */}
        <div className="bg-gray-50 rounded-xl p-4 sm:p-6">
          {incompleteAttempt ? (
            <div className="space-y-4 sm:space-y-6">
              <div className="bg-amber-100 border border-amber-300 p-3 sm:p-4 rounded-lg">
                <div className="flex items-start">
                  <div className="bg-amber-200 p-1.5 sm:p-2 rounded-full mr-3 sm:mr-4 flex-shrink-0">
                    <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-800 text-sm sm:text-base">
                      Incomplete Attempt Found
                    </h3>
                    <p className="text-amber-700 mt-1 text-xs sm:text-sm">
                      You have an unfinished quiz attempt. You can continue
                      where you left off or start fresh with a new attempt.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <button
                  onClick={() =>
                    handleContinueAttempt(incompleteAttempt.attempt_id)
                  }
                  className="bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3 sm:py-4 px-4 sm:px-6 rounded-xl transition shadow-sm flex items-center justify-center group order-2 sm:order-1 text-sm sm:text-base"
                >
                  <PlayCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2 group-hover:scale-110 transition-transform" />
                  Continue Previous Attempt
                </button>
                <button
                  onClick={() => handleStartAttempt("start")}
                  disabled={attemptReminging === 0}
                  className={`bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 sm:py-4 px-4 sm:px-6 rounded-xl transition shadow-sm flex items-center justify-center group order-1 sm:order-2 text-sm sm:text-base
                  ${
                  attemptReminging === 0
                    ? "opacity-50 cursor-not-allowed hover:bg-blue-600"
                    : ""
                }
              `}
                >
                  <Target className="w-4 h-4 sm:w-5 sm:h-5 mr-2 group-hover:scale-110 transition-transform" />
                  Start New Attempt
                </button>
              </div>
            </div>
          ) : attemptReminging > 0 ? (
            <div className="text-center">
              <button
                onClick={() => handleStartAttempt("start")}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-xl transition shadow-lg flex items-center justify-center mx-auto group text-base sm:text-lg w-full sm:w-auto"
              >
                <PlayCircle className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 group-hover:scale-110 transition-transform" />
                Start Quiz
              </button>
              <p className="text-gray-600 mt-3 text-xs sm:text-sm">
                Ready to test your knowledge? Click above to begin your quiz
                attempt.
              </p>
            </div>
          ) : (
            <div className="bg-red-50 border border-red-200 p-4 sm:p-6 rounded-lg text-center">
              <div className="bg-red-100 p-2 sm:p-3 rounded-full w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" />
              </div>
              <h3 className="font-semibold text-red-800 text-base sm:text-lg mb-2">
                No Attempts Remaining
              </h3>
              <p className="text-red-700 text-xs sm:text-sm">
                You have used all your available attempts for this quiz. Please
                contact your instructor if you need additional attempts.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
